import React, { FC, useState, useEffect } from 'react'
import { useAlert } from 'react-alert'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'

import { useTranslation } from 'react-i18next'
import Modal from '../../common/Modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { combineYearMonthAndDay, format, getYear, nowDate, prevMonth } from '../../../../utils/date'
import { useParams } from 'react-router-dom'
import { LangValue } from '../../../../states/types/common'
import { createCsvExport, fetchCsvExportList } from '../../../../states/containers/export/exportSlice'
import { getZendeskToken } from '../../../../states/apis/auth'
import { setErrors } from '../../../../states/containers/common/errorsSlice'
import { openNewTab } from '../../../../utils/url'
import AddTeamModule from './AddTeamModule'

interface Params {
  code: string
  type: string
  target_year: string
  target_month: string
  team_ids?: number[]
}

interface ExportModalState {
  display: boolean,
  type: string,
  id: number | undefined,
}

interface Props {
  exportModalState: ExportModalState
  closeCallback: VoidFunction
}

const ExportModal: FC<Props> = (props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const alert = useAlert()
  const { language } = useParams<{ language: LangValue }>()

  const {
    commonPart,
    teamList,
  } = useSelector((state: RootState) => state)

  const {
    exportModalState,
    closeCallback,
  } = props

  const initialInput = {
    code: "Shift-JIS",
    fileType: "application",
    targetYear: Number(`${format(prevMonth(nowDate()), "YYYY")}`),
    targetDate: `${format(prevMonth(nowDate()), "YYYYMM", language)}`,
  }

  const [inputCode, setInputCode] = useState<string>(initialInput.code)

  const [inputFileType, setInputFileType] = useState<string>(initialInput.fileType)

  const [selectTeams, setSelectTeams] = useState<{ id: number, name: string }[]>([])
  const [selectTeamType, setSelectTeamType] = useState<string>("all") // all, select

  const [targetYear, setTargetYear] = useState<number>(initialInput.targetYear)
  const [targetDate, setTargetDate] = useState<string>(initialInput.targetDate)

  const [displayDate, setDisplayDate] = useState<string>(
    combineYearMonthAndDay(
      `${String(initialInput.targetYear)}${(`0${prevMonth(nowDate(), 'MM')}`).slice(-2)}`, 1, t('time.date.formats.month_year'), language,
    ),
  )

  const [isCalendarDisplay, setIsCalendarDisplay] = useState(false)

  const [displayModal, setDisplayModal] = useState<boolean>(false)

  const month = t('time.date.formats.monthName').split(' ').map((v) => v)

  const closeModal = () => {
    closeCallback()
    setSelectTeams([])
    setSelectTeamType("all")
  }

  const openCalendarModal = () => {
    setIsCalendarDisplay(true)
  }

  const closeCalendarModal = () => {
    setIsCalendarDisplay(false)
  }

  const handleChangeMonth = (value: string) => {
    setIsCalendarDisplay(false)
    setTargetDate(`${String(targetYear)}${value.slice(-2)}`)
    setDisplayDate(
      combineYearMonthAndDay(`${String(targetYear)}${value.slice(-2)}`, 1, t('time.date.formats.month_year'), language),
    )
  }

  const updateFileType = (value: string) => {
    setInputFileType(value)
  }

  const updateSelectTeams = (team: { id: number, name: string }[]) => {
    setSelectTeams(team)
  }

  const saveExport = () => {
    if (!commonPart.company?.id) return
    const params: Params = {
      type: inputFileType,
      target_year: targetDate.slice(0, 4),
      target_month: targetDate.slice(-2),
      code: inputCode,
    }
    if (selectTeamType === "select" && selectTeams.length) {
      params.team_ids = selectTeams.map((v) => v.id)
    }
    dispatch(
      createCsvExport(
        params,
        () => {
          alert.success(
            <>
              {t('export.alert.start')}
            </>,
          )
          dispatch(fetchCsvExportList())
          closeModal()
        },
      ),
    )
  }

  const clickZendeskLink = (url: string) => {
    getZendeskToken()
      .then((result) => {
        openNewTab(`${result.url}&return_to=${url}`, 'post')
      })
      .catch((error) => {
        dispatch(setErrors(error))
      })
  }

  useEffect(() => {
    if (!exportModalState) return
    setInputCode(initialInput.code)
    setInputFileType(initialInput.fileType)
    setTargetYear(initialInput.targetYear)
    setTargetDate(initialInput.targetDate)
    setDisplayDate(
      combineYearMonthAndDay(
        `${String(initialInput.targetYear)}${(`0${prevMonth(nowDate(), 'MM')}`).slice(-2)}`, 1, t('time.date.formats.month_year'), language,
      ),
    )
  }, [exportModalState])

  useEffect(() => {
    setDisplayModal(exportModalState.display)
    setIsCalendarDisplay(false)
  }, [exportModalState.display])

  return (
    <Modal
      title={t('export.csv.title')}
      show={displayModal}
      wrapperClass="export_data"
      closeCallback={closeModal}
      closeable
    >
      {
        commonPart.payment_finished ? (
          <>
            <div className="item">
              <div className="title">
                {t('export.select_type.title')}
              </div>
              <div className="detail row_1">
                <select value={inputFileType} onChange={(e) => updateFileType(e.target.value)}>
                  <option value="application">アプリケーション使用情報</option>
                  <option value="work">勤怠時間情報</option>
                  <option value="detail">アプリケーション詳細情報</option>
                </select>
                <div className="text_link">
                  {t('export.csv.explanation')}
                  <span
                    role="none"
                    onClick={() => clickZendeskLink("https://qasee.zendesk.com/hc/ja/articles/43704596547609")}
                  >
                    {t('export.csv.here')}
                  </span>
                </div>
              </div>
            </div>
            <hr />
            <div className="item">
              <div className="title team_select">
                出力対象を選択してください
                <div className="toggle_area">
                  <button
                    type="button"
                    onClick={() => setSelectTeamType("all")}
                    className={selectTeamType === "all" ? "on" : ""}
                  >
                    全てのユーザー
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectTeamType("select")}
                    className={selectTeamType === "select" ? "on" : ""}
                  >
                    チーム選択
                  </button>
                </div>
              </div>
              {
                selectTeamType === "select" && (
                  <div className="detail row_1">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        border: "solid 1px #CCCCCC",
                        borderRadius: "4px",
                        width: "100%",
                        padding: "8px",
                        flexFlow: "wrap",
                      }}
                    >
                      {
                        selectTeams.map((value) => (
                          <div
                            style={{
                              backgroundColor: "#FFFFFF",
                              border: "solid 1px #CCCCCC",
                              borderRadius: "20px",
                              padding: "4px 8px",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                            key={`condition_${value.id}`}
                          >
                            {value.name}
                            <span
                              style={{
                                cursor: "pointer",
                              }}
                              onClick={() => updateSelectTeams([...selectTeams.filter((v) => v.id !== value.id)])}
                              role="none"
                            >
                              <FontAwesomeIcon icon={['fas', 'times']} />
                            </span>
                          </div>
                        ))
                      }
                      <AddTeamModule
                        teamIdList={selectTeams.map((v) => v.id)}
                        callbackTeams={(teams) => updateSelectTeams(teams)}
                        teamList={teamList.teams.map((v) => ({ id: v.team_id, name: v.name }))}
                      />
                    </div>
                  </div>
                )
              }
            </div>
            <hr />
            <div className="item">
              <div className="title">
                {t('export.select_month.title')}
              </div>
              <div className="detail row_1">
                <div className="calendar_text">
                  <button
                    type="button"
                    className="display button in_modal"
                    onClick={() => openCalendarModal()}
                  >
                    {displayDate}
                    <FontAwesomeIcon icon={['fas', 'angle-down']} className={`in_modal ${isCalendarDisplay && 'open'}`} />
                  </button>
                </div>
                <div className={`calendar_box in_modal ${isCalendarDisplay || 'none'}`}>
                  <div className="range">
                    <div className="text">
                      {t(`range.calendar.header_text.monthly`)}
                    </div>
                  </div>
                  <div className="monthly">
                    <div className="select_month">
                      <div>
                        <button
                          type="button"
                          onClick={() => setTargetYear(targetYear - 1)}
                          disabled={Number(format(commonPart.company?.created_at, 'YYYY')) >= targetYear}
                        >
                          <FontAwesomeIcon icon={['fas', 'angle-left']} className="in_modal" />
                        </button>
                      </div>
                      <div>
                        {`${targetYear}${t('time.time.short.year')}`}
                      </div>
                      <div>
                        <button
                          type="button"
                          onClick={() => setTargetYear(targetYear + 1)}
                          disabled={getYear() <= targetYear}
                        >
                          <FontAwesomeIcon icon={['fas', 'angle-right']} className="in_modal" />
                        </button>
                      </div>
                    </div>
                    {
                      month.map((v, i) => {
                        const target = String(targetYear) + (`0${String(i + 1)}`).slice(-2)
                        const isClickableMonth = Number(nowDate('YYYYMM')) >= Number(target)
                          && Number(format(commonPart.company?.created_at, 'YYYYMM')) <= Number(target)
                        const isSelected = targetDate === target
                        return (
                          <div className="monthly_button" key={v}>
                            <button
                              type="button"
                              className={isSelected ? "selected" : ""}
                              onClick={() => handleChangeMonth(target)}
                              disabled={!isClickableMonth}
                            >
                              {`${v}${t('time.time.short.month')}`}
                            </button>
                          </div>
                        )
                      })
                    }
                  </div>
                </div>
                <div className={`screen in_modal transparent ${isCalendarDisplay || 'none'}`} role="presentation" onClick={closeCalendarModal} />
              </div>
            </div>
            <hr />
            <div className="item">
              <div className="title">
                {t('export.character_code.title')}
                <p className="annotation">
                  {t('export.character_code.annotation')}
                </p>
              </div>
              <div className="detail row_1 code">
                <label htmlFor="code_1">
                  <input
                    type="radio"
                    name="code"
                    id="code_1"
                    value="Shift-JIS"
                    defaultChecked
                    onClick={(e) => setInputCode(e.currentTarget.value)}
                  />
                  Shift-JIS
                </label>
                <label htmlFor="code_2">
                  <input
                    type="radio"
                    name="code"
                    id="code_2"
                    value="UTF-8"
                    onClick={(e) => setInputCode(e.currentTarget.value)}
                  />
                  UTF-8
                </label>
              </div>
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                fontSize: "14px",
                textAlign: "center",
              }}
            >
              トライアル期間中はご利用になれません
            </div>
            <div
              style={{
                marginTop: "16px",
                textAlign: "center",
              }}
            >
              出力できるデータに関する情報は、
              <span
                role="none"
                onClick={() => clickZendeskLink("https://qasee.zendesk.com/hc/ja/articles/43698838138649")}
                style={{
                  color: "#337ab7",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                こちら
              </span>
              をご参照ください。
            </div>
          </>
        )
      }
      {
        commonPart.payment_finished ? (
          <>
            <button type="button" className="button cancel" onClick={closeModal}>
              {t('setting.personal.custom_alert.modal.button.cancel')}
            </button>
            <button
              type="button"
              className="button"
              onClick={saveExport}
            >
              出力する
            </button>
          </>
        ) : (
          <>
            <button type="button" className="button cancel" onClick={closeModal}>
              閉じる
            </button>
          </>
        )
      }
    </Modal>
  )
}

export default ExportModal
